import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";

import dropdown from "../../assets/images/dropdown.png";
import * as Icon from "react-feather";

import { useTranslation, Translation } from "react-i18next";
import { capitalizeFirst, upperCase } from "../../utils/manipulateText";

const MultiselectBox = ({
  mode = "multiple",
  title = "",
  buttonCustomClass = "",
  buttonCustomStyle = {},
  customStyle = {},
  customClass = "",
  options = [],
  onConfig,
  onChange,
  defaultSelectedItems,
  selectedItems: _selectedItems,
  filterPlaceholderText,
  fullWidth = false,
}) => {
  const [selectedItems, setSelectedItems] = useState(
    defaultSelectedItems ?? []
  );
  const [screenWidth, setScreenWidth] = useState();
  const [open, setOpen] = useState(false);
  const selectorRef = useRef(null);
  const { t } = useTranslation();

  const handleClickOutside = (event) => {
    if (selectorRef.current && !selectorRef.current.contains(event.target)) {
      if (open) {
        setOpen(false);
      }
    }
  };

  useEffect(() => {
    const resize = () => {
      setScreenWidth(window.screen.width);
    };
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (_selectedItems) {
      setSelectedItems(_selectedItems);
    }
  }, [_selectedItems]);

  const onSelect = (item) => {
    setSelectedItems((prevItems) => {
      const selected = prevItems.some((itemId) => itemId === item?.id);
      let returnValue;
      if (mode === "single") {
        if (selected) {
          returnValue = [];
        } else {
          returnValue = [item];
        }
      } else {
        if (selected) {
          returnValue = prevItems.filter((itemId) => itemId !== item?.id);
        } else {
          returnValue = [...prevItems, item?.id];
        }
      }
      onChange(returnValue);
      return returnValue;
    });
  };

  const [filterValue, setFilterValue] = useState("");

  const filteredOptions = options.filter(
    (el) =>
      el.label.toLowerCase().includes(filterValue.toLowerCase()) ||
      el.id.toString().includes(filterValue)
  );

  return (
    <div
      ref={selectorRef}
      className={`w-full relative ${
        onConfig && !fullWidth ? "md:w-1/2" : "md:w-full"
      }`}
    >
      {!onConfig && (
        <button
          className={`sm:inline-flex items-center justify-start ${
            selectedItems?.length > 0 ? "pt-1 px-1" : "p-1"
          } border bg-gray-200 rounded-md transition-all ease-out transition-fast text-xs font-bold py-2`}
          onClick={() => setOpen((prev) => !prev)}
        >
          <div className="flex items-center">
            {selectedItems?.length > 0 && (
              <label className="transition-all ease-out transition-fast text-xs font-bold px-2 py-0 rounded m-0 my-0 mx-1 bg-white shadow">
                {selectedItems.length}
              </label>
            )}
            <Translation>
              {(t) => (
                <span className="ml-1">
                  {upperCase(t(title.toLowerCase()))}
                </span>
              )}
            </Translation>
            <img
              style={{ width: "15%" }}
              alt=""
              className="mx-auto"
              src={dropdown}
            />
          </div>
        </button>
      )}
      {onConfig && (
        <button
          className={`block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 ${buttonCustomClass}`}
          style={{ buttonCustomStyle }}
          onClick={() => setOpen((prev) => !prev)}
        >
          {selectedItems.length > 0 && (
            <label
              className={`px-2 mr-3 transition-all ease-out transition-fast text-sm font-bold rounded bg-white shadow`}
            >
              {selectedItems.length}
            </label>
          )}
          {capitalizeFirst(title)}
        </button>
      )}
      <div
        className={`border p-3 rounded-md text-sm font-semibold bg-white absolute z-50 ${
          !open ? "hidden" : ""
        } overflow-y-scroll ${customClass}`}
        style={{
          width: screenWidth <= 600 ? "200px" : "400px",
          maxHeight: "50vh",
          ...customStyle,
        }}
      >
        <div
          style={{ fontSize: 11.5 }}
          className="md:font-light text-md py-1 pl-5"
        >
          Select {title.toLowerCase()} in order to view selected data.
        </div>
        <div className="p-2 mx-2">
          <input
            type="text"
            className="border p-1 w-full rounded-md"
            placeholder={capitalizeFirst(
              t(filterPlaceholderText || "adPlacementFilterText")
            )}
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
          />
        </div>
        <span className="inline-block pb-1 align-middle m-2">
          {filteredOptions.map((el) => (
            <div
              key={el.id}
              className="md:bg-gray-50 p-1 m-1 mb-2 float-left cursor-pointer"
              onClick={() => onSelect(el)}
              data-select={el.id}
            >
              <button
                className={`transition-all ease-out transition-fast text-xs font-bold px-3 py-1 rounded m-1 cursor-pointer ${
                  selectedItems?.indexOf(el.id) !== -1
                    ? "bg-white shadow"
                    : "bg-gray"
                }`}
              >
                <label className="mr-1 float-left cursor-pointer">
                  {el.label}
                </label>
                {selectedItems?.indexOf(el.id) !== -1 && (
                  <Icon.X
                    width="12"
                    height="12"
                    className="mx-auto my-auto text-gray-600"
                  />
                )}
              </button>
            </div>
          ))}
        </span>
      </div>
    </div>
  );
};

MultiselectBox.defaultProps = {
  mode: "multiple",
};

MultiselectBox.propTypes = {
  mode: PropTypes.oneOf(["multiple", "single"]),
  title: PropTypes.string.isRequired,
  buttonCustomStyle: PropTypes.object,
  buttonCustomClass: PropTypes.string,
  customStyle: PropTypes.object,
  customClass: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  defaultSelectedItems: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onConfig: PropTypes.bool,
};

export default MultiselectBox;
